/* eslint-disable id-length */
/* eslint-disable max-lines-per-function */
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { useAuth } from '../../../Hooks/useAuth';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { MIS_TIME_SHEET } from '../../../Api/resources/endPoint';
import { MODULE_NAMES } from '../../../Constants/modules';
import { ReactComponent as CalendarActivityIcon } from '../../../Assets/images/svg/calendar_view.svg';
import { ReactComponent as ListIcon } from '../../../Assets/images/svg/list_view.svg';
import { ReactComponent as CalendarEmployeeIcon } from '../../../Assets/images/svg/calendar_employee.svg';
import './timesheetMIS.scss';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

const PAGE_SIZE = 10;
const INITIAL_PAGINATION = { page: 1, limit: PAGE_SIZE, total: 0 };

const buildServiceURL = (service, path) => {
  const url = appendServiceNameToURL(service);
  if (process.env.NODE_ENV === 'production') {
    return `${process.env.REACT_APP_API_URL}${url}${path}`;
  }
  const port = appendServicePortToURL(service);
  const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  return `${baseUrl}:${port}${url}${path}`;
};

const sheetNameMapping = (viewType) => {
  switch (viewType) {
  case 'list_view':
    return 'TimeSheet-MIS-List-View';
  case 'calendar_view_employee_wise':
    return 'TimeSheet-MIS-Calendar-View-Employee-Wise';
  case 'calendar_view_activity_wise':
    return 'TimeSheet-MIS-Calendar-View-Activity-Wise';
  default:
    return 'Unknown-View';
  }
};

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;
  rootNode.querySelectorAll('.button-group button').forEach((btn) => {
    const isReset = Boolean(btn.querySelector('.pi-refresh'));
    const isFilter = btn.classList.contains('table-utility-filter-btn');
    const isNew = btn.classList.contains('table-utility-add-new-btn');
    const isViewToggle = btn.classList.contains('tsr-view-toggle-btn');
    const isExport = !isFilter && !isReset && !isNew && !isViewToggle && Boolean(btn.querySelector('svg'));
    btn.classList.toggle('time-sheet-mis-reset-btn', isReset);
    btn.classList.toggle('time-sheet-mis-export-btn', isExport);
    btn.classList.toggle('time-sheet-mis-add-btn', isNew);
  });
};

const normalizePublicHoliday = (publicHolidayList) => {
  if (!Array.isArray(publicHolidayList) || publicHolidayList.length === 0) return '';
  return publicHolidayList
    .map((entry) => {
      if (typeof entry === 'string') return entry;
      if (entry?.date) return dayjs(entry.date).format('DD-MMM-YYYY');
      if (entry?.holidayDate) return dayjs(entry.holidayDate).format('DD-MMM-YYYY');
      if (entry?.name) return entry.name;
      return null;
    })
    .filter(Boolean)
    .join(', ') || '';
};

const extractHeaderSummary = (response, tableData) => {
  const directSummary = response?.data?.headerSummary ?? response?.data?.data?.headerSummary;
  if (directSummary) return directSummary;
  if (Array.isArray(tableData)) {
    const rowSummary = tableData.find((row) => row?.headerSummary)?.headerSummary;
    if (rowSummary) return rowSummary;
  }
  return null;
};

const mapHeaderSummaryToState = (headerSummary) => {
  if (!headerSummary) return { projects: '', lastEffortDate: '', publicHoliday: '' };
  const projectList = Array.isArray(headerSummary?.projectDetailsList)
    ? headerSummary.projectDetailsList
      .map((item) => item?.projectName)
      .filter((name) => typeof name === 'string' && name.trim().length > 0)
    : [];
  const uniqueProjectNames = [...new Set(projectList.map((name) => name.trim()))];

  const projects = uniqueProjectNames.length > 0
    ? uniqueProjectNames.join(', ')
    : headerSummary?.projectName || headerSummary?.projectDetails?.projectName || '';

  const lastEffortDate = headerSummary?.lastEffortDate
    ? dayjs(headerSummary.lastEffortDate).format('DD-MMM-YYYY hh:mm A')
    : '';
  const publicHoliday = normalizePublicHoliday(headerSummary?.publicHolidayList);

  return { projects, lastEffortDate, publicHoliday };
};

const applyTotalRowClass = (container) => {
  if (!container) return;
  const tbody = container.querySelector('.p-datatable-tbody');
  if (!tbody) return;
  const rows = [...tbody.querySelectorAll('tr')];
  let totalRow = null;

  rows.forEach((row) => {
    const hasTotalText = [...row.cells].some((cell) => cell.innerText?.trim() === 'Total');
    if (hasTotalText) {
      row.classList.add('time-sheet-total-row');
      totalRow = row;
    }
  });
  if (totalRow && tbody.lastElementChild !== totalRow) {
    tbody.appendChild(totalRow);
  }
};

const TIME_SHEET_REPORT_AVAILABLE_FILTERS = {
  list_view: [
    'date', 'projectName', 'employeeName', 'projectType',
    'projectStatus', 'projectVertical', 'effortSource',
  ],
  calendar_view_employee_wise: [
    'date', 'employeeName', 'projectName', 'projectType',
    'projectStatus', 'projectVertical', 'effortSource',
  ],
  calendar_view_activity_wise: [
    'date', 'employeeName', 'activity_name', 'projectName',
    'projectType', 'projectStatus', 'projectVertical', 'effortSource',
  ],
};

const buildExportFileName = (fileName) => {
  const timestamp = dayjs().format('DD-MMM-YYYY-hh-mm-ss-A');
  return `${fileName}-${timestamp}.xlsx`;
};

const TimeSheetSummary = ({ viewType, summary }) => {
  return (viewType === 'calendar_view_employee_wise' || viewType === 'calendar_view_activity_wise') && (
    <div className="time-sheet-mis-summary-wrapper">
      <div className="time-sheet-mis-project-details-wrapper">
        <span className="label">Projects :</span>
        <span
          className="value"
          data-summary-field="projects"
          title={summary?.projects || ''}
        >
          {summary?.projects}
        </span>
      </div>
      <div className="time-sheet-mis-last-effort-details-wrapper">
        <span className="label">Last Effort Uploaded On :</span>
        <span
          className="value"
          data-summary-field="lastEffortDate"
          title={summary?.lastEffortDate || ''}
        >
          {summary?.lastEffortDate}
        </span>
      </div>
      <div className="time-sheet-mis-public-holiday-details-wrapper">
        <span className="label">Public Holiday :</span>
        <span
          className="value"
          data-summary-field="publicHoliday"
          title={summary?.publicHoliday || ''}
        >
          {summary?.publicHoliday}
        </span>
      </div>
    </div>
  );
};

TimeSheetSummary.propTypes = {
  viewType: PropTypes.string.isRequired,
  summary: PropTypes.shape({
    projects: PropTypes.string,
    lastEffortDate: PropTypes.string,
    publicHoliday: PropTypes.string,
  }),
};

const buildExportSheetName = (sheetName) => sheetName;

const TimeSheetReportMIS = () => {
  const { userProfile } = useAuth();

  // Default to list_view when permitted, otherwise fall back to whichever calendar view is allowed
  const getDefaultViewType = () => {
    const listPermissions = userProfile?.permissions?.[MODULE_NAMES.MIS_TIME_SHEET_LIST_VIEW] || [];
    const employeeWisePermissions
      = userProfile?.permissions?.[MODULE_NAMES.MIS_TIME_SHEET_CALENDAR_VIEW_EMPLOYEE_WISE] || [];
    const activityWisePermissions
      = userProfile?.permissions?.[MODULE_NAMES.MIS_TIME_SHEET_CALENDAR_VIEW_ACTIVITY_WISE] || [];
    if (listPermissions.includes('read') || listPermissions.includes('view')) return 'list_view';
    if (employeeWisePermissions.includes('read') || employeeWisePermissions.includes('view')) {
      return 'calendar_view_employee_wise';
    }
    if (activityWisePermissions.includes('read') || activityWisePermissions.includes('view')) {
      return 'calendar_view_activity_wise';
    }
    return 'list_view';
  };

  const [viewType, setViewType] = useState(getDefaultViewType);

  // Permissions
  const listViewPermissions = userProfile?.permissions?.[MODULE_NAMES.MIS_TIME_SHEET_LIST_VIEW] || [];
  const employeeWiseViewPermissions
    = userProfile?.permissions?.[MODULE_NAMES.MIS_TIME_SHEET_CALENDAR_VIEW_EMPLOYEE_WISE] || [];
  const activityWiseViewPermissions
    = userProfile?.permissions?.[MODULE_NAMES.MIS_TIME_SHEET_CALENDAR_VIEW_ACTIVITY_WISE] || [];

  const canViewInListView = listViewPermissions.includes('read') || listViewPermissions.includes('view');
  const canViewInEmployeeWise = employeeWiseViewPermissions.includes('read')
    || employeeWiseViewPermissions.includes('view');
  const canViewInActivityWise = activityWiseViewPermissions.includes('read')
    || activityWiseViewPermissions.includes('view');

  const canViewForType = (type) => {
    if (type === 'list_view') return canViewInListView;
    if (type === 'calendar_view_employee_wise') return canViewInEmployeeWise;
    if (type === 'calendar_view_activity_wise') return canViewInActivityWise;
    return false;
  };

  const canViewInCurrentView = canViewForType(viewType);

  const [globalExternalFilters, setGlobalExternalFilters] = useState([]);
  const capturedGlobalFiltersRef = useRef([]);

  const paginationRef = useRef(INITIAL_PAGINATION);
  const [, forceUpdate] = useState(0);
  const triggerPaginationRender = useCallback(() => forceUpdate((n) => n + 1), []);

  const tableContainerRef = useRef(null);
  const [summary, setSummary] = useState({ projects: '', lastEffortDate: '', publicHoliday: '' });
  const summaryRef = useRef({ projects: '', lastEffortDate: '', publicHoliday: '' });
  const tableStateRef = useRef(null);
  const domEnhancementRafRef = useRef(null);
  const summaryHostRef = useRef(null);

  const baseURL = useMemo(
    () => buildServiceURL(serviceName.PROJECT_MS, `/api${MIS_TIME_SHEET}/view/${viewType}`),
    [viewType],
  );

  const normalizeCapturedFilters = useCallback((filters) => {
    if (!Array.isArray(filters)) return [];

    return filters
      .filter((filter) => filter && typeof filter === 'object' && typeof filter.field === 'string')
      .map((filter) => {
        const minimalFilter = { field: filter.field };

        if (Array.isArray(filter.rangePlaceholders) && filter.rangePlaceholders.length > 0) {
          minimalFilter.rangePlaceholders = [...filter.rangePlaceholders];
        }

        if (Array.isArray(filter.range) && filter.range.some((value) => value !== null
        && value !== undefined && value !== '')) {
          minimalFilter.range = [...filter.range];
        }

        if (Array.isArray(filter.selectedValues) && filter.selectedValues.length > 0) {
          minimalFilter.selectedValues = [...filter.selectedValues];
        }

        if (filter.selectedValue !== null && filter.selectedValue !== undefined && filter.selectedValue !== '') {
          minimalFilter.selectedValue = filter.selectedValue;
        }

        if (filter.condition !== null && filter.condition !== undefined && filter.condition !== '') {
          minimalFilter.condition = filter.condition;
        }

        if (filter.value !== null && filter.value !== undefined && filter.value !== '') {
          minimalFilter.value = filter.value;
        }

        return minimalFilter;
      });
  }, []);

  const buildViewScopedFilters = useCallback((filters, nextViewType) => {
    const availableFilters = TIME_SHEET_REPORT_AVAILABLE_FILTERS[nextViewType] || [];
    const normalizedFilters = normalizeCapturedFilters(filters);
    return normalizedFilters.filter((filter) => availableFilters.includes(filter.field));
  }, [normalizeCapturedFilters]);

  const handleViewChange = useCallback((newViewType) => {
    if (newViewType === viewType) return;

    if (!canViewForType(newViewType)) {
      return;
    }

    const nextExternalFilters = buildViewScopedFilters(
      capturedGlobalFiltersRef.current,
      newViewType,
    );
    setGlobalExternalFilters(nextExternalFilters);

    setSummary({ projects: '', lastEffortDate: '', publicHoliday: '' });

    paginationRef.current = { ...INITIAL_PAGINATION };
    triggerPaginationRender();
    setViewType(newViewType);
  }, [
    buildViewScopedFilters,
    triggerPaginationRender,
    viewType,
    canViewInListView,
    canViewInEmployeeWise,
    canViewInActivityWise,
  ]);

  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    const tableData = response?.data?.tableData ?? response?.data?.data?.tableData ?? [];
    const totalRow = response?.data?.totalRow ?? response?.data?.data?.totalRow;
    const headerSummary = extractHeaderSummary(response, tableData);
    const nextSummary = mapHeaderSummaryToState(headerSummary);

    setSummary((prevSummary) => {
      if (
        prevSummary.projects === nextSummary.projects
        && prevSummary.lastEffortDate === nextSummary.lastEffortDate
        && prevSummary.publicHoliday === nextSummary.publicHoliday
      ) {
        return prevSummary;
      }
      return nextSummary;
    });

    if (tableData.length && totalRow) {
      const merged = [...tableData, { ...totalRow, isTotalRow: true }];
      if (response?.data?.tableData) response.data.tableData = merged;
      else if (response?.data?.data?.tableData) response.data.data.tableData = merged;
    }

    const pg = response.data?.pagination
               ?? response.data?.paginationConfig
               ?? response.data?.data?.pagination
               ?? response.data?.data?.paginationConfig;

    const next = {
      page: pg?.page ?? pg?.currentPage ?? paginationRef.current.page,
      limit: pg?.limit ?? PAGE_SIZE,
      total: pg?.total ?? pg?.count ?? response.meta?.total ?? tableData.length ?? 0,
    };

    const prev = paginationRef.current;
    if (prev.page !== next.page || prev.limit !== next.limit || prev.total !== next.total) {
      paginationRef.current = next;
      triggerPaginationRender();
    } else {
      paginationRef.current = next;
    }
  }, [triggerPaginationRender]);

  const handlePaginationChange = useCallback((newPagination) => {
    paginationRef.current = newPagination;
    triggerPaginationRender();
  }, [triggerPaginationRender]);

  const handleFilterUpdate = useCallback(({ action, filters }) => {
    // Reset summary immediately so stale header data isn't displayed during fetch
    setSummary({ projects: '', lastEffortDate: '', publicHoliday: '' });

    if (action === 'reset') {
      capturedGlobalFiltersRef.current = [];
      setGlobalExternalFilters([]);
      paginationRef.current = { ...INITIAL_PAGINATION };
      triggerPaginationRender();
      return;
    }

    if (action === 'apply') {
      const normalizedFilters = normalizeCapturedFilters(filters);

      capturedGlobalFiltersRef.current = normalizedFilters;

      setGlobalExternalFilters([...normalizedFilters]);

      paginationRef.current = {
        ...paginationRef.current,
        page: 1,
      };

      triggerPaginationRender();
    }
  }, [normalizeCapturedFilters, triggerPaginationRender]);

  const additionalBodyElements = useMemo(() => (
    <TimeSheetSummary viewType={viewType} summary={summary} />
  ), [viewType, summary]);

  useEffect(() => {
    summaryRef.current = summary;
  }, [summary, viewType]);

  // DOM Enhancements handled safely inside RAF and disconnecting observer during mutations
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    let observer = null;
    const scheduleEnhancements = () => {
      if (domEnhancementRafRef.current) return;
      domEnhancementRafRef.current = window.requestAnimationFrame(() => {
        domEnhancementRafRef.current = null;
        if (observer) observer.disconnect();

        applyToolbarButtonClasses(container);
        applyTotalRowClass(container);

        const buttonGroup = container.querySelector('.button-group');
        if (buttonGroup) {
          const currentWidth = buttonGroup.offsetWidth;
          const lockedWidth = parseFloat(buttonGroup.getAttribute('data-locked-w') || '0');
          if (currentWidth > lockedWidth) {
            buttonGroup.setAttribute('data-locked-w', String(currentWidth));
            buttonGroup.style.minWidth = `${currentWidth}px`;
          }
        }

        if (observer) observer.observe(container, { childList: true, subtree: true });
      });
    };

    scheduleEnhancements();
    observer = new MutationObserver(() => scheduleEnhancements());
    observer.observe(container, { childList: true, subtree: true });

    return () => {
      if (domEnhancementRafRef.current) {
        window.cancelAnimationFrame(domEnhancementRafRef.current);
        domEnhancementRafRef.current = null;
      }
      if (observer) observer.disconnect();
    };
  }, [viewType]);

  useEffect(() => {
    return () => {
      if (summaryHostRef.current?.parentNode) {
        summaryHostRef.current.parentNode.removeChild(summaryHostRef.current);
      }
      summaryHostRef.current = null;
    };
  }, []);

  const additionalElements = useMemo(() => [
    <React.Fragment key="view-toggle">
      {viewType !== 'list_view' && (
        <div className="view-note-label">
          <span className="warn-label">Note: <span className="highlight">H refers to Holiday</span></span>
        </div>
      )}
      <div className="view-button-wrapper">
        {canViewInListView && (
          <button
            className={`tsr-view-toggle-btn${viewType === 'list_view' ? ' active' : ''}`}
            onClick={() => handleViewChange('list_view')}
            title="List View"
            type="button"
          >
            <ListIcon />
          </button>
        )}
        {canViewInEmployeeWise && (
          <button
            className={`tsr-view-toggle-btn${viewType === 'calendar_view_employee_wise' ? ' active' : ''}`}
            onClick={() => handleViewChange('calendar_view_employee_wise')}
            title="Calendar View Employee Wise"
            type="button"
          >
            <CalendarEmployeeIcon />
          </button>
        )}
        {canViewInActivityWise && (
          <button
            className={`tsr-view-toggle-btn${viewType === 'calendar_view_activity_wise' ? ' active' : ''}`}
            onClick={() => handleViewChange('calendar_view_activity_wise')}
            title="Calendar View Activity Wise"
            type="button"
          >
            <CalendarActivityIcon />
          </button>
        )}
      </div>
    </React.Fragment>,
  ], [viewType, handleViewChange, canViewInListView, canViewInEmployeeWise, canViewInActivityWise]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: true,
      baseURL,
      headers: { token: userProfile?.access_token || '' },
      additionalPayloadData: { employeeId: userProfile?.employeeId || '',
        filters: globalExternalFilters},
      userInfo: {
        id: userProfile?.id || '',
        emailId: userProfile?.email || '',
        permissions: userProfile?.permissions || [],
        roleDetails: userProfile?.roleDetails || [],
        employeeId: userProfile?.employeeId || '',
      },
      handler: {
        handleFilteredApiResponse,
        setTableStateHandler: (getState) => {
          tableStateRef.current = getState;
        },
        onFilterUpdate: handleFilterUpdate,
      },
      apiEndpoints: {
        getAllViews: { endpoint: '', enabled: false },
        getFilteredData: { endpoint: '', enabled: true },
        saveOrUpdateView: { endpoint: '', enabled: false },
        deleteView: { endpoint: '', enabled: false },
        download: {
          endpoint: 'export',
          enabled: true,
          filename: () => buildExportFileName(sheetNameMapping(viewType)),
          sheetName: () => buildExportSheetName(sheetNameMapping(viewType)),
        },
      },
      showToolBar: true,
      showPagination: true,
      toolBarSettings: {
        showAddNew: false,
        showFilterOption: true,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showDownloadExcel: true,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showToolBarHeading: true,
        toolBarHeading: 'Time Sheet MIS Report',
        showReset: true,
        additionalElements,
      },
    },
    showFilterOption: true,
    tableSettings: {
      enableTreeTable: false,
      size: 'large',
      loaderSkeleton: { rows: 10, bodyCells: 1, columns: 8 },
      pageSize: PAGE_SIZE,
      paginationConfig: {
        ...paginationRef.current,
        onPaginationChange: handlePaginationChange,
        showPageSizeSelector: false,
      },
      additionalBodyElements,
      showExpandCollapse: false,
      tableScrollHeight: '400px',
      maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
    },
  }), [
    baseURL,
    viewType,
    userProfile,
    additionalElements,
    handleFilteredApiResponse,
    handleFilterUpdate,
    handlePaginationChange,
    globalExternalFilters,
    additionalBodyElements,
  ]);

  return (
    <div className="time-sheet-mis-outer">
      <div className="time-sheet-mis-table">
        <div
          className={`time-sheet-mis-table-utility ${viewType}`}
          ref={tableContainerRef}
        >
          {canViewInCurrentView
            ? (
              <TableUtility
                key={viewType}
                configuration={configuration}
                externalFilters={globalExternalFilters}
              />
            )
            : <div className="time-sheet-mis-no-permission">You do not have permission to view this.</div>}
        </div>
      </div>
    </div>
  );
};

export default TimeSheetReportMIS;
